import { z } from 'zod';

export const RECONCILIATION_STATUSES = [
  'in_progress',
  'completed',
  'voided',
] as const;
export type ReconciliationStatus = (typeof RECONCILIATION_STATUSES)[number];

const moneyString = z
  .string()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a money value with up to 4 decimals');

// ── Create (open a reconciliation session) ───────────────────────────────────

export const createReconciliationInputSchema = z.object({
  // Reconcile any GL account (bank, credit card, loan, clearing account…). The
  // service resolves/creates the internal bank-account record it hangs on.
  glAccountId: z.string().uuid(),
  statementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  statementBeginningBalance: moneyString.default('0'),
  statementEndingBalance: moneyString,
  notes: z.string().max(1000).optional(),
});
export type CreateReconciliationInput = z.infer<
  typeof createReconciliationInputSchema
>;

// ── Update statement details (in-progress only) ──────────────────────────────

export const updateReconciliationInputSchema = z
  .object({
    statementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    statementBeginningBalance: moneyString.optional(),
    statementEndingBalance: moneyString.optional(),
    notes: z.string().max(1000).nullable().optional(),
  })
  .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
  });
export type UpdateReconciliationInput = z.infer<
  typeof updateReconciliationInputSchema
>;

// ── Set cleared items (replace the cleared set) ──────────────────────────────

export const setReconciliationItemsInputSchema = z.object({
  journalEntryIds: z.array(z.string().uuid()),
});
export type SetReconciliationItemsInput = z.infer<
  typeof setReconciliationItemsInputSchema
>;

// ── Complete ─────────────────────────────────────────────────────────────────

export const completeReconciliationInputSchema = z.object({
  notes: z.string().max(1000).optional(),
});
export type CompleteReconciliationInput = z.infer<
  typeof completeReconciliationInputSchema
>;

// ── Void ─────────────────────────────────────────────────────────────────────

export const voidReconciliationInputSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
});
export type VoidReconciliationInput = z.infer<
  typeof voidReconciliationInputSchema
>;

// ── Correct a completed reconciliation (typo fix) ────────────────────────────
// Rule 6 mandates append-only: we VOID the original, then INSERT a new
// in-progress reconciliation pre-populated from it (same items pre-checked).
// The user reviews and clicks Complete on the new one. Two hash-chain entries
// are appended (void of original + completion of corrected) — full audit.
export const correctReconciliationInputSchema = z.object({
  statementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  statementBeginningBalance: moneyString.optional(),
  statementEndingBalance: moneyString.optional(),
  notes: z.string().max(1000).nullable().optional(),
  reason: z.string().trim().min(1).max(1000),
});
export type CorrectReconciliationInput = z.infer<
  typeof correctReconciliationInputSchema
>;

// ── Response shapes ──────────────────────────────────────────────────────────

export const reconciliationSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  bankAccountId: z.string().uuid(),
  // The GL account being reconciled (derived from the bank-account record).
  glAccountId: z.string().uuid().nullable().optional(),
  glAccountCode: z.string().nullable().optional(),
  glAccountName: z.string().nullable().optional(),
  statementDate: z.string(),
  statementBeginningBalance: z.string(),
  statementEndingBalance: z.string(),
  status: z.enum(RECONCILIATION_STATUSES),
  clearedBalance: z.string().nullable(),
  difference: z.string().nullable(),
  notes: z.string().nullable(),
  completedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  voidedAt: z.string().nullable(),
  priorReconciliationId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * The vault document linked to this reconciliation as its statement, or null
   * when none is on file. The packets attach exactly this document, so the
   * screen can say whether the evidence exists instead of leaving it to be
   * discovered at month end.
   */
  statementDocument: z
    .object({ id: z.string().uuid(), fileName: z.string() })
    .nullable()
    .optional(),
});
export type Reconciliation = z.infer<typeof reconciliationSchema>;

// ── Link filed statements ────────────────────────────────────────────────────
//
// Statements uploaded through Documents sit in the vault with no reconciliation
// behind them. Pair each completed reconciliation that has no statement with the
// vault statement dated the same day, and re-file its packet with the pages
// behind the proof.

export const linkFiledStatementsInputSchema = z.object({
  /** Report the pairings without writing anything. */
  dryRun: z.boolean().default(false),
});
export type LinkFiledStatementsInput = z.infer<typeof linkFiledStatementsInputSchema>;

export const linkedStatementSchema = z.object({
  reconciliationId: z.string().uuid(),
  statementDate: z.string(),
  glAccountCode: z.string().nullable(),
  glAccountName: z.string().nullable(),
  vaultDocumentId: z.string().uuid(),
  fileName: z.string(),
  /** False when the vault packet could not be rebuilt; the link still stands. */
  packetRefiled: z.boolean(),
});
export type LinkedStatement = z.infer<typeof linkedStatementSchema>;

export const unresolvedStatementSchema = z.object({
  reconciliationId: z.string().uuid(),
  statementDate: z.string(),
  glAccountCode: z.string().nullable(),
  glAccountName: z.string().nullable(),
  reason: z.string(),
});
export type UnresolvedStatement = z.infer<typeof unresolvedStatementSchema>;

export const linkFiledStatementsResultSchema = z.object({
  dryRun: z.boolean(),
  linked: z.array(linkedStatementSchema),
  unresolved: z.array(unresolvedStatementSchema),
});
export type LinkFiledStatementsResult = z.infer<typeof linkFiledStatementsResultSchema>;

/** A candidate journal entry (posting to the reconciled GL account) that can be cleared. */
export const reconcilableItemSchema = z.object({
  journalEntryId: z.string().uuid(),
  entryNumber: z.string(),
  date: z.string(),
  description: z.string(),
  /** Signed movement on the GL account, in normal-balance terms. */
  signedMovement: z.string(),
  cleared: z.boolean(),
});
export type ReconcilableItem = z.infer<typeof reconcilableItemSchema>;

export const hashChainVerifyEntrySchema = z.object({
  reconciliationId: z.string().uuid(),
  sequence: z.number(),
  valid: z.boolean(),
});

export const hashChainVerifyResultSchema = z.object({
  valid: z.boolean(),
  entries: z.array(hashChainVerifyEntrySchema),
});
export type HashChainVerifyResult = z.infer<typeof hashChainVerifyResultSchema>;

// ── Reconciliation worksheet (cleared/reconciled, undeposited funds, uncleared checks) ──

export interface ReconWorksheetCheck {
  id: string;
  checkNumber: string;
  checkDate: string;
  payeeName: string;
  amount: string;
}

export interface ReconWorksheetDeposit {
  id: string;
  depositDate: string;
  payerName: string;
  amount: string;
}

export interface ReconWorksheetTxn {
  id: string;
  date: string;
  description: string;
  amount: string;
}

export interface ReconciliationWorksheet {
  glAccountId: string;
  glAccountCode: string;
  glAccountName: string;
  /** Already reconciled bank transactions on this account (most recent first, capped). */
  reconciledTransactions: ReconWorksheetTxn[];
  reconciledCount: number;
  clearedChecks: ReconWorksheetCheck[];
  clearedDeposits: ReconWorksheetDeposit[];
  /** Outstanding checks — written, not yet cleared the bank. */
  unclearedChecks: ReconWorksheetCheck[];
  unclearedChecksTotal: string;
  /** Deposits recorded but not yet cleared (in transit). */
  depositsInTransit: ReconWorksheetDeposit[];
  depositsInTransitTotal: string;
  /** Balance of the Undeposited Funds (1900) account — money received, not yet deposited. */
  undepositedFundsBalance: string;
}
